import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { Shield, ExternalLink } from "lucide-react";

const Section = ({ id, title, children, highlight = false }) => (
  <section id={id} className="py-8 border-b border-[#1E1E2E] last:border-0">
    <h2 className={`text-xl font-bold mb-4 ${highlight ? "text-[#71DED6]" : "text-[#F4F4F8]"}`}>
      {title}
    </h2>
    <div className="space-y-4 text-[#8B8BA7] leading-relaxed text-[15px]">
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1E1E2E] flex-shrink-0" />
    <span>{children}</span>
  </li>
);

const Strong = ({ children }) => (
  <span className="text-[#F4F4F8] font-medium">{children}</span>
);

export default function PrivacyPolicyPage() {
  usePageTitle("Privacy Policy | CleanBid.ai");
  return (
    <div className="bg-[#0A0A0F] pt-20">
      {/* Hero */}
      <div className="border-b border-[#1E1E2E] py-14">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-[#71DED6]" />
            <span className="text-xs uppercase tracking-widest text-[#71DED6] font-semibold">Legal</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#F4F4F8] mb-3">
            Privacy Policy
          </h1>
          <p className="text-[#8B8BA7] text-sm">Last updated: January 1, 2026</p>
        </div>
      </div>

      {/* Content */}
      <article className="py-12" data-testid="privacy-policy-content">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">

          <Section id="introduction" title="Introduction & Who We Are">
            <p>
              Welcome to CleanBid. CleanBid ("<Strong>CleanBid</Strong>," "<Strong>we</Strong>," "<Strong>us</Strong>," or "<Strong>our</Strong>") operates the website at mycleanbid.ai and the CleanBid revenue platform for home service businesses (the "<Strong>Service</Strong>").
            </p>
            <p>
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service. Please read this policy carefully. If you disagree with its terms, please discontinue use of the Service.
            </p>
            <p>
              We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last updated" date at the top of this page. Your continued use of the Service after any changes constitutes your acceptance of the revised policy.
            </p>
          </Section>

          <Section id="information-collected" title="Information We Collect">
            <p>We may collect the following categories of information when you register for or use the Service:</p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet><Strong>Personal Identification Information:</Strong> Your full name, email address, phone number, and business name.</Bullet>
              <Bullet><Strong>Business Information:</Strong> Service type, service area, pricing, and operational details you enter into the platform.</Bullet>
              <Bullet><Strong>Customer Data:</Strong> Names, phone numbers, email addresses, and service history of your customers that you upload or create within CleanBid. You are responsible for ensuring you have appropriate rights to input this data.</Bullet>
              <Bullet><Strong>Payment Information:</Strong> Billing address and payment card details processed through our third-party payment processor, Stripe. CleanBid does not store full payment card numbers on our servers.</Bullet>
              <Bullet><Strong>Usage Data:</Strong> Information about how you interact with the Service, including pages visited, features used, clicks, timestamps, IP address, browser type, and device identifiers.</Bullet>
              <Bullet><Strong>Communications:</Strong> Records of SMS messages, notes, and interactions conducted through the CleanBid platform.</Bullet>
              <Bullet><Strong>Cookies and Tracking Data:</Strong> Data collected automatically through cookies and similar technologies as described in the Cookies section below.</Bullet>
            </ul>
          </Section>

          <Section id="how-we-use" title="How We Use Your Information">
            <p>CleanBid uses the information we collect for the following purposes:</p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet>To create and manage your account and provide access to the Service.</Bullet>
              <Bullet>To process transactions and send related billing information.</Bullet>
              <Bullet>To send SMS quotes, follow-ups, and review requests on your behalf to your customers.</Bullet>
              <Bullet>To send you administrative messages, technical notices, updates, security alerts, and support communications.</Bullet>
              <Bullet>To respond to your inquiries and provide customer support.</Bullet>
              <Bullet>To monitor and analyze usage trends, platform performance, and feature adoption.</Bullet>
              <Bullet>To detect, prevent, and address technical issues, fraud, or violations of our Terms of Service.</Bullet>
              <Bullet>To improve, personalize, and expand the Service.</Bullet>
              <Bullet>To comply with applicable legal obligations.</Bullet>
            </ul>
          </Section>

          {/* SMS Section — Highlighted */}
          <section id="sms-communications" className="py-8 border-b border-[#1E1E2E]">
            <h2 className="text-xl font-bold mb-4 text-[#71DED6]">
              SMS Communications
            </h2>
            <div className="bg-[#111118] border border-[#71DED6]/30 rounded-2xl p-6 space-y-4 text-[#8B8BA7] leading-relaxed text-[15px]">
              <p>
                CleanBid is an SMS-based platform. The following terms apply to all SMS communications facilitated through the Service:
              </p>
              <ul className="space-y-3 ml-2">
                <Bullet>
                  <Strong>Opt-In:</Strong> By creating a CleanBid account and enabling SMS features, you consent to receive SMS messages from CleanBid related to your account, including transactional notifications, platform updates, and support messages. Your customers opt in to receive SMS messages from your CleanBid business number when they provide you with their contact information or initiate contact via SMS.
                </Bullet>
                <Bullet>
                  <Strong>Message Frequency:</Strong> Message frequency varies based on your usage of the platform and the volume of business activity. Your customers may receive quotes, follow-up messages, and review requests depending on their interactions with your business.
                </Bullet>
                <Bullet>
                  <Strong>Message & Data Rates:</Strong> Message and data rates may apply. Messaging rates are determined by the recipient's mobile carrier and their data plan. CleanBid is not responsible for any messaging fees charged by carriers.
                </Bullet>
                <Bullet>
                  <Strong>Opt-Out:</Strong> Recipients can opt out of SMS messages at any time by replying <Strong>STOP</Strong> to any message. CleanBid will honor opt-out requests and cease sending messages to that number. To re-enable messages, recipients may reply <Strong>START</Strong>.
                </Bullet>
                <Bullet>
                  <Strong>Help:</Strong> Recipients can reply <Strong>HELP</Strong> to any message for assistance or contact us directly at <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">info@cleanbid.ai</a>.
                </Bullet>
                <Bullet>
                  <Strong>No Sale of SMS Opt-In Data:</Strong> We do not sell, rent, trade, or share SMS opt-in data or phone numbers collected through the opt-in process with third parties for their marketing purposes.
                </Bullet>
                <Bullet>
                  <Strong>Carrier Liability:</Strong> Carriers are not liable for delayed or undelivered messages. SMS delivery is subject to carrier network availability and policies. CleanBid does not guarantee delivery of any SMS message.
                </Bullet>
                <Bullet>
                  <Strong>Your Responsibility:</Strong> As a CleanBid user, you are responsible for ensuring that all individuals you message through the platform have provided appropriate consent to receive SMS messages from your business, in compliance with applicable law including the TCPA.
                </Bullet>
              </ul>
            </div>
          </section>

          <Section id="sharing" title="How We Share Your Information">
            <p>
              <Strong>We do not sell your personal information.</Strong> We may share your information in the following limited circumstances:
            </p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet><Strong>Service Providers:</Strong> We share data with trusted third-party vendors who assist us in operating the Service, including cloud hosting providers, payment processors (Stripe), SMS gateway providers, and analytics tools. These providers are contractually obligated to protect your information.</Bullet>
              <Bullet><Strong>Business Transfers:</Strong> If CleanBid is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you before your information is transferred and becomes subject to a different privacy policy.</Bullet>
              <Bullet><Strong>Legal Requirements:</Strong> We may disclose your information if required by law, subpoena, or other legal process, or if we have a good-faith belief that disclosure is necessary to protect the rights, property, or safety of CleanBid, our users, or the public.</Bullet>
              <Bullet><Strong>With Your Consent:</Strong> We may share your information with third parties when you have explicitly consented to such sharing.</Bullet>
            </ul>
          </Section>

          <Section id="data-retention" title="Data Retention">
            <p>
              We retain your personal information for as long as your account is active or as needed to provide the Service. We also retain information as necessary to comply with legal obligations, resolve disputes, enforce agreements, and for legitimate business purposes.
            </p>
            <p>
              Customer data you have entered into the platform will be retained for the duration of your subscription. Upon account termination, you may request deletion of your data in accordance with your rights described below. We will delete or anonymize your personal information within 30 days of a valid deletion request, unless we are required to retain it for legal purposes.
            </p>
          </Section>

          <Section id="cookies" title="Cookies and Tracking">
            <p>
              We use cookies and similar tracking technologies to track activity on our Service and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier.
            </p>
            <p>You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, some portions of the Service may not function properly.</p>
            <p>We use the following types of cookies:</p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet><Strong>Strictly Necessary Cookies:</Strong> Required for the Service to function. These cannot be disabled.</Bullet>
              <Bullet><Strong>Analytical/Performance Cookies:</Strong> Allow us to recognize and count visitors and understand how users move around the Service, helping us improve how it works.</Bullet>
              <Bullet><Strong>Functionality Cookies:</Strong> Used to recognize you when you return to the Service and remember your preferences.</Bullet>
            </ul>
          </Section>

          <Section id="third-party" title="Third-Party Services">
            <p>
              Our Service may contain links to or integrations with third-party websites and services. This Privacy Policy does not apply to those third parties, and we encourage you to review their respective privacy policies. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party services.
            </p>
            <p>Key third-party services we use include:</p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet><Strong>Stripe</Strong> — Payment processing. Stripe's privacy policy governs their handling of your payment data.</Bullet>
              <Bullet><Strong>SMS Carriers & Gateway Providers</Strong> — For message delivery. Carrier policies govern delivery of SMS messages.</Bullet>
              <Bullet><Strong>Cloud Infrastructure Providers</Strong> — For hosting and data storage.</Bullet>
            </ul>
          </Section>

          <Section id="user-rights" title="Your Rights">
            <p>Depending on your jurisdiction, you may have the following rights regarding your personal information:</p>
            <ul className="space-y-2 ml-2 mt-2">
              <Bullet><Strong>Access:</Strong> Request a copy of the personal information we hold about you.</Bullet>
              <Bullet><Strong>Correction:</Strong> Request correction of inaccurate or incomplete personal information.</Bullet>
              <Bullet><Strong>Deletion:</Strong> Request deletion of your personal information, subject to certain legal limitations.</Bullet>
              <Bullet><Strong>Portability:</Strong> Request that we provide your data in a portable format.</Bullet>
              <Bullet><Strong>Objection / Restriction:</Strong> Object to or request restriction of certain processing of your personal information.</Bullet>
            </ul>
            <p>
              To exercise any of these rights, please contact us at <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">info@cleanbid.ai</a>. We will respond to your request within 30 days.
            </p>
          </Section>

          <Section id="children" title="Children's Privacy">
            <p>
              The CleanBid Service is not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If you become aware that a child under 13 has provided us with personal information without parental consent, please contact us at <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">info@cleanbid.ai</a> and we will take steps to remove that information.
            </p>
          </Section>

          <Section id="changes" title="Changes to This Policy">
            <p>
              We reserve the right to update this Privacy Policy at any time. When we do, we will revise the "Last updated" date at the top of this page. For material changes, we will provide more prominent notice, which may include an email notification to the address associated with your account or an in-app notification.
            </p>
            <p>
              Your continued use of the Service after any changes constitutes your acceptance of the updated Privacy Policy.
            </p>
          </Section>

          <Section id="contact" title="Contact Us">
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-[#111118] border border-[#1E1E2E] rounded-xl p-5 mt-3 space-y-1.5">
              <p><Strong>CleanBid.ai</Strong></p>
              <p>Email: <a href="mailto:info@cleanbid.ai" className="text-[#71DED6] hover:underline">info@cleanbid.ai</a></p>
              <p>Website: <a href="https://mycleanbid.ai" className="text-[#71DED6] hover:underline">mycleanbid.ai</a></p>
            </div>
          </Section>

          {/* See also */}
          <div className="pt-8 mt-4 border-t border-[#1E1E2E] flex flex-col sm:flex-row items-start sm:items-center gap-3 text-sm text-[#8B8BA7]">
            <span>See also:</span>
            <div className="flex items-center gap-4">
              <Link to="/terms-of-service" className="text-[#71DED6] hover:underline flex items-center gap-1">
                Terms of Service <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>

        </div>
      </article>
    </div>
  );
}
